import { getCustomersLoyaltyCheck } from '@agos/api-client/customers'
import { getGeocodeReverse } from '@agos/api-client/geo'
import { useGetSalesChannelsPublic } from '@agos/api-client/orders'
import { apiErrorField } from '@agos/api-client'
import {
  useGetCampaignsActivePromo,
  useGetCampaignsHappyHoursPublic,
} from '@agos/api-client/marketing'
import {
  usePostCouponsValidate,
  useGetBurgerBoxConfig,
  useGetCategories,
  useGetGiftsProductRulesPublic,
  useGetProducts,
} from '@agos/api-client/menu'
import type { CreateOrderRequest } from '@agos/api-client/model'
import {
  useGetDeliveryZones,
  useGetDeliveryZonesDepot,
  usePostOrders,
} from '@agos/api-client/orders'
import { usePostPaymentsInitiate } from '@agos/api-client/payments'
// Tags follow the backend's controllers, not this app's modules: the referral check and the loyalty
// settings are the customers controller's, and the payment settings are the payments controller's.
import {
  useGetCustomerAuthMe,
  useGetCustomerAuthMeSavedAddresses,
  useGetLoyaltyPublicInfo,
  usePostCustomersRegister,
  usePostOrdersValidateReferral,
} from '@agos/api-client/customers'
import { useGetSettingsPaymentsPublic } from '@agos/api-client/payments'
import { useGetSettingsLunchPublic, useGetSettingsOpeningHours } from '@agos/api-client/platform'
import {
  T,
  centsToEuros,
  formatCents,
  formatCount,
  boxFixedPricesOf,
  toDateParam,
  formatNamedDayTime,
  geocodeOnce,
  tt,
} from '@agos/shared'
import {
  AlertTriangle,
  Info,
  ArrowLeft,
  Banknote,
  CalendarClock,
  Clock,
  CheckCircle2,
  CreditCard,
  Loader2,
  Mail,
  MapPin,
  Minus,
  Package,
  Soup,
  Phone,
  LocateFixed,
  Plus,
  StickyNote,
  Store,
  UtensilsCrossed,
  Trash2,
  Truck,
  User,
} from 'lucide-react'
import { cn,
  AddressField,
  toast,
} from '@agos/ui'
import { useEffect, useMemo, useState, type InputHTMLAttributes} from 'react'
import { Link, Redirect, useLocation } from 'wouter'

import {
  addressSummary,
  announcementOf,
  announcementShows,
  rememberOrder,
  giftLine,
  giftOffers,
  giftRulesOf,
  depositCentsOf,
  lunchLineOf,
  type PricedLine,
} from '@agos/customer-core'
import { groupBasket } from './basket-groups'
import {
  addressesOf,
  defaultAddress,
  allLunchMeals,
  balanceNote,
  currentLevelKey,
  composedSubtotalCents,
  lunchMealsIn,
  lunchOrderLines,
  lunchSettingsOf,
  lunchWeeksOf,
  lunchSubtotalCents,
  maxRedeemable,
  multiplierNote,
  quickAmounts,
  type QuickAmount,
  tierFor,
  pointsValueCents,
  lineKey,
  priceLines,
  ratesOf,
  redemptionNote,
  subtotalCents,
  useMinute,
  type PricedProduct,
} from '@agos/customer-core'
import {
  NO_DISCOUNTS,
  payableCents,
  replacedNote,
  discountBreakdown,
  personalDiscountLabel,
  replacesEverything,
  roundingNote,
  totalDiscountCents,
} from '@agos/customer-core'
import { mayOrder, openNote, openState, type OpeningHours } from '@agos/customer-core'
import { useCart } from '@agos/customer-core'
import { useCustomerAuth } from '@agos/customer-core'
import {
  couponCents,
  couponFrom,
  couponRefusal,
  referralDiscountCents,
  stillApplies,
  type AppliedCoupon,
} from './coupon'
import {
  addressToResolve,
  basketProgress,
  deliveryFor,
  deliveryNote,
  progressFraction,
  zoneLine,
  zonesOf,
} from './delivery'
import { orderPayload, submitProblems, type CheckoutFacts } from './order-payload'
import {
  paymentMethodLabel,
  paymentMethodNote,
  paymentMethodsFor,
  paymentSettingsOf,
  selectedPaymentMethod,
  type PaymentMethod,
} from './payment-methods'
import {
  activePromoOf,
  blackFridayCents,
  blackFridayLabel,
  freeDeliveryApplies,
  happyHoursCents,
  happyHoursLabel,
  happyHoursOf,
  happyHoursPointsNote,
} from './promo'
import {
  lunchPreorderDate,
  lunchServingWindow,
  preorderProblem,
  scheduledAtOf,
} from './preorder'
import { chosenType, orderTypesOf } from './order-types'

/**
 * The checkout.
 *
 * Every rule on this screen lives in a tested module beside it — `coupon`, `delivery`, `preorder`,
 * `discounts`, `order-payload` — and this file only wires them to inputs. That is deliberate: legacy's
 * `Checkout.tsx` is 2 700 lines with the arithmetic inline, which is how five discount sources ended up
 * with two rounding rules (FB-C04) and nobody noticed.
 *
 * **The total is the server's formula**, in the server's order:
 * `payable(max(0, subtotal + delivery − discount + deposits))`. The five-cent step lands once, on the
 * total; every discount keeps its cent.
 *
 * The deposit is zero and will stay zero until a backend change: no public route exposes the
 * product-to-surcharge join, so the app cannot know a product carries one. That is FB-C05's client half,
 * recorded in `BLOCKED.md` rather than counted as done.
 */

/** A key per checkout attempt, so a double tap answers the first order instead of placing a second. */
function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * A labelled field, as legacy writes them: an icon, the label, and a required marker.
 *
 * This screen had bare placeholders — which vanish the moment somebody types, so a half-filled form
 * stops saying what its boxes are, and a screen reader is told nothing at all. FB-C53.
 */
const FIELD_CLASS =
  'w-full px-4 py-3 rounded-xl bg-background border border-border transition-colors outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary'

function CheckoutField(props: {
  id: string
  icon: typeof User
  label: string
  hint?: string
  children: React.ReactNode
}) {
  const Icon = props.icon
  return (
    <div className="space-y-2">
      <label htmlFor={props.id} className="text-sm font-semibold flex items-center gap-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {/* The marker is **inside** the label legacy passes - `<T>Meno a priezvisko *</T>` - not a span
            beside it. Rendering it separately put a gap before the asterisk and coloured it red, and
            `slovak-parity.test.ts` missed the string difference by 0.006: 17 characters of 19 is 0.894
            against a 0.9 threshold. FB-C63. */}
        {props.label}
      </label>
      {props.children}
      {props.hint !== undefined && <p className="text-xs text-muted-foreground">{props.hint}</p>}
    </div>
  )
}

export function CheckoutPage() {
  const [, navigate] = useLocation()
  const lines = useCart((state) => state.lines)
  const add = useCart((state) => state.add)
  const clear = useCart((state) => state.clear)
  const changeBy = useCart((state) => state.changeBy)
  const removeLine = useCart((state) => state.remove)
  // §9.166 — the checkout's first caller. The cart has exported it since the box was built.
  const removeBoxGroup = useCart((state) => state.removeBoxGroup)
  const user = useCustomerAuth((state) => state.user)

  const { data: productData, isLoading: productsLoading } = useGetProducts({})
  // §9.142 — what the basket earns right now. Same rule and same helper the menu uses; legacy
  // duplicates it into both of its pages, which is how two copies drift.
  const { data: giftRuleData } = useGetGiftsProductRulesPublic()
  const { data: categoryData } = useGetCategories()
  const { data: zoneData } = useGetDeliveryZones()
  // §10A.121 - the depot, read rather than derived. A restaurant moves about never, so this is
  // cached for the session: the point only decides which of two overlapping zones wins.
  const { data: depot } = useGetDeliveryZonesDepot({ query: { staleTime: 30 * 60_000 } })
  // Legacy re-reads this every 60 seconds (§14.1) — a campaign can start while a basket is open.
  const { data: promoData } = useGetCampaignsActivePromo({ query: { refetchInterval: 60_000 } })
  // §9.254 — an hour ends while somebody is filling in the form, so this is re-read on the same
  // interval the opening hours are. The server decides; this is what the summary shows.
  const { data: happyData } = useGetCampaignsHappyHoursPublic({ query: { refetchInterval: 60_000 } })
  const {
    data: hoursData,
    isLoading: hoursLoading,
    isError: hoursError,
    // Legacy re-reads the hours every minute here as well as on the menu (§14.1). A checkout open across
    // closing time must find out from the server, not from the copy it loaded twenty minutes ago.
  } = useGetSettingsOpeningHours({ query: { refetchInterval: 60_000 } })
  const { data: lunchData, isLoading: lunchLoading } = useGetSettingsLunchPublic()
  /**
   * §10A.107 - the box configuration, for its fixed price.
   *
   * **The checkout has to read this even though the menu screen already did.** This is the screen a
   * customer reads a total off before they pay, and `ServerPricing` will apply the fixed price
   * whether or not this page knows about it. Without this the basket would show the sum of the parts
   * and the bill would say something else, which is §10A.80's defect from the customer's side.
   */
  const { data: boxData } = useGetBurgerBoxConfig()
  const { data: paymentsData } = useGetSettingsPaymentsPublic()
  // `/loyalty/public-info`, **never** `/settings/loyalty`. The C0 spec records the decision and the
  // reason is FB-138: the settings endpoint answers 44 fields including a live mail credential, and
  // *public* in an endpoint name is a claim worth verifying rather than trusting. The profile already
  // reads the right one; this page briefly read the wrong one.
  const { data: loyaltyData } = useGetLoyaltyPublicInfo()
  // Only a signed-in customer has points to spend; the query is idle otherwise.
  const account = useGetCustomerAuthMe({ query: { enabled: user !== null, retry: false } })
  const validateReferral = usePostOrdersValidateReferral()
  const joinProgramme = usePostCustomersRegister()
  const savedAddresses = useGetCustomerAuthMeSavedAddresses({
    query: { enabled: user !== null, retry: false },
  })

  const validateCoupon = usePostCouponsValidate()
  // One key per mount, so a double tap answers the first order rather than placing a second. Generated
  // here rather than per submit: a key that changes on retry is not an idempotency key.
  const [idempotencyKey] = useState(newIdempotencyKey)
  const placeOrder = usePostOrders({
    request: { headers: { 'X-Idempotency-Key': idempotencyKey } },
  })
  const initiatePayment = usePostPaymentsInitiate()

  /**
   * §9.50 — the channels the franchise offers on the web, not a hard-coded pair.
   *
   * `type` is a slug now, and every branch below asks the chosen channel what it *does*. That is
   * §9.5's rule reaching the customer site: a franchise that adds *Terasa* and marks it for the web
   * gets it here without a release.
   */
  const { data: channelData } = useGetSalesChannelsPublic()
  const orderTypes = useMemo(() => orderTypesOf(channelData), [channelData])
  const [type, setType] = useState<string>('delivery')
  const channel = chosenType(orderTypes, type)
  const needsAddress = channel.requiresDeliveryAddress
  // **Typed-or-known, rather than filled-in-an-effect.** Legacy prefills all four of these from the
  // session; this port filled only the e-mail, so a signed-in customer retyped their name, phone and
  // address on every order — and `defaultAddress`, written to find the one they marked, was called by
  // nobody (FB-C58).
  //
  // `null` means *not touched*. The alternative — writing the profile into state once it arrives — is
  // the field that resets under a cursor mid-typing, and it is what the shell's setState-in-effect rule
  // exists to prevent. Deriving it needs no effect and cannot race the request.
  const [typedName, setName] = useState<string | null>(null)
  const [typedPhone, setPhone] = useState<string | null>(null)
  const [typedEmail, setEmail] = useState<string | null>(null)
  const [typedAddress, setAddress] = useState<string | null>(null)
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(null)
  useMinute()

  const [chosenPayment, setChosenPayment] = useState<PaymentMethod | null>(null)
  const [redeem, setRedeem] = useState(0)
  // §9.71 — one box for both kinds of code.
  const [codeInput, setCodeInput] = useState('')
  const [codeError, setCodeError] = useState('')
  const [referral, setReferral] = useState<string | null>(null)
  const [referralProblem, setReferralProblem] = useState('')
  const [referralCents, setReferralCents] = useState(0)
  const [joinLoyalty, setJoinLoyalty] = useState(false)
  const [notes, setNotes] = useState('')
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null)
  const [couponError, setCouponError] = useState('')
  const [preorder, setPreorder] = useState<{ date: string; time: string } | null>(null)
  /**
   * Which lunch day the default was applied for — §9.219.
   *
   * Derived rather than set in an effect: the default applies once per day the basket asks for, and
   * an effect calling `setPreorder` would fight a customer who switched it off. Comparing the day
   * against the one already handled says the same thing during render.
   */
  const [lunchDayHandled, setLunchDayHandled] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState('')

  const products = useMemo(
    () => (Array.isArray(productData) ? (productData as PricedProduct[]) : []),
    [productData],
  )
  const zones = useMemo(() => zonesOf(zoneData), [zoneData])

  // §9.142 — the gift the basket has earned and not yet taken. Legacy evaluates the same rule on its
  // checkout as well as its menu; a customer who crossed the threshold and came straight here would
  // otherwise never be told. Recomputed rather than carried over, so it also follows a line removed
  // on the way.
  const checkoutGifts = useMemo(() => {
    const categories = Array.isArray(categoryData)
      ? (categoryData as { id?: number; name?: string }[])
      : []
    const countable = lines.map((line) => ({
      line,
      categoryName:
        categories.find(
          (category) =>
            category.id ===
            (products.find((product) => product.id === line.productId) as
              | { categoryId?: number }
              | undefined)?.categoryId,
        )?.name ?? '',
    }))
    // §9.167 — every rule the basket has earned. This read `[0]`, so a basket qualifying under two
    // rules was told about one and the second stayed invisible until the first was accepted. Legacy
    // maps the whole list; nothing caught it here because the fixture has one rule.
    return giftOffers(giftRulesOf(giftRuleData), countable)
  }, [giftRuleData, categoryData, lines, products])
  const hours = hoursLoading || hoursError ? null : (hoursData as OpeningHours | null)

  // Which methods the franchise will take, and which one is selected — recomputed rather than stored,
  // because the order type can change *after* a method was picked and a restaurant that does not take
  // cards on pickup would otherwise leave the customer holding one.
  const payments = useMemo(() => paymentSettingsOf(paymentsData), [paymentsData])
  const methods = paymentMethodsFor(payments, needsAddress)
  const paymentMethod = selectedPaymentMethod(payments, needsAddress, chosenPayment)

  // Loyalty points: what this customer has, and how many the programme will let them spend.
  const rates = useMemo(() => ratesOf(loyaltyData), [loyaltyData])
  const profileRow = (
    account.data as
      | { user?: { pointsBalance?: unknown; orderCount?: unknown; loyaltyLevel?: unknown } }
      | undefined
  )?.user
  const balance = typeof profileRow?.pointsBalance === 'number' ? profileRow.pointsBalance : 0
  /**
   * §9.218 — what a signed-in member is greeted with, under the e-mail.
   *
   * All four figures are already on `account.data.user`, which this component reads for the balance
   * two lines above; the tier is resolved from the franchise's own thresholds rather than from a
   * hard-coded five, exactly as the profile page does it.
   */
  const memberOrderCount = typeof profileRow?.orderCount === 'number' ? profileRow.orderCount : 0
  const memberLevel = currentLevelKey(
    rates,
    memberOrderCount,
    typeof profileRow?.loyaltyLevel === 'string' ? profileRow.loyaltyLevel : null,
  )
  // §9.250 — the tier as the franchise writes it, from the reader the profile uses, so the two
  // screens cannot end up with different words for the same tier.
  const memberTier = tierFor(loyaltyData, memberLevel)
  const redeemNote = redemptionNote(rates, balance)
  // §9.278 — `redeemable` and `redeemedCents` moved below the other discounts, because the ceiling
  // is now the cart's as well as the balance's and the cart is not known this early.

  // The addresses this customer has ordered to before. Legacy offers them here and this port did not,
  // so a signed-in customer retyped their address on every order.
  const saved = useMemo(() => addressesOf(savedAddresses.data), [savedAddresses.data])

  const profile = (account.data as { user?: Record<string, unknown> } | undefined)?.user
  const knownName = typeof profile?.name === 'string' ? profile.name : (user?.name ?? '')
  const knownPhone = typeof profile?.phone === 'string' ? profile.phone : ''
  const knownEmail = typeof profile?.email === 'string' ? profile.email : (user?.email ?? '')
  // The address they marked as theirs. Legacy pre-trusts it — it came from an order that arrived.
  const knownAddress = defaultAddress(saved)?.address ?? ''

  const customerName = typedName ?? knownName
  const customerPhone = typedPhone ?? knownPhone
  const customerEmail = typedEmail ?? knownEmail
  const customerAddress = typedAddress ?? knownAddress

  const fixedBoxPrices = useMemo(() => boxFixedPricesOf(boxData), [boxData])
  const priced = priceLines(lines, products, fixedBoxPrices)

  // The daily menu is a second catalogue: `/products` does not carry the day's meals, and their price
  // depends on what else is in the basket (a soup is cheaper beside a main). Both halves are totalled
  // and both are sent — the split into two lines for a partly-bundled soup happens here, once, from
  // the same rule the menu screen showed.
  const lunchMeals = useMemo(() => allLunchMeals(lunchData), [lunchData])
  /**
   * §9.219 — the day this basket's daily-menu lines are for, and the hours that menu is served.
   *
   * Read off the basket rather than remembered from the menu screen: a meal is added from a day and
   * then somebody walks away, adds a pizza and comes back.
   */
  const lunchDay = useMemo(
    // `toDateParam` rather than a fourth copy of the same three lines — two modules already carry
    // their own `localDate` and this is the shared one.
    () => lunchPreorderDate(lines, lunchWeeksOf(lunchData), toDateParam(new Date())),
    [lines, lunchData],
  )
  const lunchWindow = useMemo(() => lunchServingWindow(lunchSettingsOf(lunchData)), [lunchData])
  const lunchBundlePrice = useMemo(
    () => lunchSettingsOf(lunchData).soupBundlePrice ?? null,
    [lunchData],
  )
  const lunchBasket = useMemo(() => lunchMealsIn(lines, lunchMeals), [lines, lunchMeals])
  const lunchCents = lunchSubtotalCents(lunchBasket, lunchBundlePrice)

  // A kombo pizza and anything else the customer assembled: no catalogue to price it from, so the price
  // it was composed at travels on the line itself.
  const composedLines = useMemo(() => lines.filter((line) => line.composed !== undefined), [lines])
  const composedCents = composedSubtotalCents(lines)
  // The lunch half is priced by its own rule, so it joins the subtotal rather than the catalogue.
  const subtotal = subtotalCents(priced) + lunchCents + composedCents
  const couponLines = priced.map((entry) => ({
    productId: entry.line.productId,
    quantity: entry.line.quantity,
    unitCents: entry.unitCents,
  }))

  // A product coupon whose item has left the basket is not applied, and is not shown as if it were.
  const liveCoupon = stillApplies(coupon, couponLines) ? coupon : null
  /**
   * §10A.121 - the address and the depot decide an overlap, and the server uses both.
   *
   * The depot is **read from the server** rather than geocoded here: the tie-break falls back to
   * depot distance when no zone name appears in the address, and a client computing that from its
   * own geocode would quote a fee the customer is not charged. `GET /delivery-zones/depot` exists so
   * both sides read the same two numbers. A depot that cannot be resolved answers nulls, which both
   * sides treat identically - fall back to the smallest zone.
   */
  const depotPoint =
    typeof depot?.lat === 'number' && typeof depot.lng === 'number'
      ? { lat: depot.lat, lng: depot.lng }
      : null
  const delivery = deliveryFor(needsAddress ? point : null, zones, subtotal, {
    address: customerAddress,
    depot: depotPoint,
  })
  const rawDeliveryCents = delivery.kind === 'ok' ? delivery.feeCents : 0

  const exclusive = replacesEverything(user, subtotal)

  // The two campaigns that change what an order costs. The **server** decides them — `OrderCampaigns`
  // evaluates both at order creation, written that way because legacy applied Black Friday in the
  // browser alone and stored the undiscounted total. Nothing here is sent; this is the summary agreeing
  // with what will be charged, rather than the checkout quoting a higher number than the bill (FB-C44).
  const promo = useMemo(() => activePromoOf(promoData), [promoData])
  const codeApplied = liveCoupon !== null || referral !== null
  const promoCents = exclusive ? 0 : blackFridayCents(promo, subtotal, codeApplied)
  // §9.254 — a personal discount that replaces everything replaces this too, as it does the
  // other four; happy hours must not be the one that leaks past §9.65's rule.
  const happy = happyHoursOf(happyData)
  const happyCents = exclusive ? 0 : happyHoursCents(happy, subtotal)
  const deliveryFree = freeDeliveryApplies(promo, subtotal, needsAddress)
  const deliveryCents = deliveryFree ? 0 : rawDeliveryCents

  /* §9.278, owner O23 — what the order can still absorb in points.
   *
   * **Every other discount is subtracted and the points are not**, which is the half legacy's own
   * comment warns about: a ceiling computed from a total that already has the points taken off
   * shrinks on every drag and the slider fights the person using it. */
  const beforePoints = exclusive
    ? 0
    : couponCents(liveCoupon, couponLines, subtotal) +
      (referral === null ? 0 : referralCents) +
      promoCents +
      happyCents
  const absorbableCents = Math.max(0, subtotal + rawDeliveryCents - beforePoints)
  const redeemable = exclusive ? 0 : maxRedeemable(rates, balance, absorbableCents)
  const redeemedCents = pointsValueCents(rates, Math.min(redeem, redeemable))

  const discount = totalDiscountCents(
    user,
    {
      ...NO_DISCOUNTS,
      // Every source goes through one place, because the server caps the **sum** once and against
      // subtotal plus delivery — adding any of them to the total separately is how a preview drifts.
      couponCents: exclusive ? 0 : couponCents(liveCoupon, couponLines, subtotal),
      pointsCents: exclusive ? 0 : redeemedCents,
      referralCents: exclusive || referral === null ? 0 : referralCents,
      blackFridayCents: promoCents,
      happyHoursCents: happyCents,
    },
    { subtotalCents: subtotal, deliveryCents },
  )
  // §9.182 — the deposits the server will add. This passed a literal `0` while
  // `OrderCreationService.surchargeTotal` charged them, so a basket with a deposit was quoted 2,15 €
  // and billed 2,30 €.
  const deposits = depositCentsOf(priced, products)
  const exact = Math.max(0, subtotal + deliveryCents + deposits - discount)
  const total = payableCents({
    subtotalCents: subtotal,
    deliveryCents,
    depositCents: deposits,
    discountCents: discount,
  })

  const state = openState(hours, new Date())
  // §9.243 — the owner's notice reaches the checkout too. A customer holding a basket who is
  // being told they cannot order should not have to go back to the menu to read why.
  const announcement = announcementOf(hoursData)
  // A guest, and a programme that is actually running. Read twice — the offer itself and the reason the
  // e-mail field gives for wanting an address.
  const joinLoyaltyOffered = user === null && rates !== null && rates.isActive

  /**
   * §9.163, the owner's point 47 — whether the typed address already has an account.
   *
   * Debounced, because an address arrives a character at a time and this is one question, not twelve.
   * Only while the programme is on offer: a signed-in visitor is already that person, and a franchise
   * with loyalty off has nothing to check.
   */
  const [existingAccount, setExistingAccount] = useState(false)
  const [isLocating, setIsLocating] = useState(false)

  /**
   * §9.164 — legacy's *Moja poloha*, which is three steps and not one.
   *
   * The old version set `point` and stopped, so the address box stayed empty and the order button
   * stayed disabled: the button looked broken because the part that fills the field had never been
   * written. Legacy takes the browser's coordinates to the server, writes `display_name` into the
   * field, and keeps the point — which is what makes an address Google has never heard of usable.
   *
   * Every failure says which one it was. Setting `point` to null on refusal, as this did before, is
   * indistinguishable on screen from nothing having happened at all.
   */
  const locate = () => {
    if (typeof navigator.geolocation === 'undefined') {
      toast({
        variant: 'destructive',
        title: tt('Geolokácia nedostupná'),
        description: tt('Váš prehliadač nepodporuje určenie polohy.'),
      })
      return
    }
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        // The point is kept whatever the lookup says: the zone is decided by coordinates, and a
        // provider that cannot name the spot has not made the delivery impossible.
        setPoint({ lat: latitude, lng: longitude })
        void getGeocodeReverse({ lat: String(latitude), lon: String(longitude) })
          .then((place) => {
            // `ReverseGeocode` always answers — the coordinates become `display_name` when nothing
            // can name the point, so there is no empty-string branch to guard here.
            setAddress(place.display_name)
            toast({
              title: tt('Poloha zistená'),
              description: tt('Adresa bola vyplnená podľa aktuálnej polohy.'),
            })
          })
          .catch(() => {
            toast({
              variant: 'destructive',
              title: tt('Chyba'),
              description: tt('Nepodarilo sa získať adresu z polohy.'),
            })
          })
          .finally(() => setIsLocating(false))
      },
      () => {
        setIsLocating(false)
        toast({
          variant: 'destructive',
          title: tt('Prístup zamietnutý'),
          description: tt('Povoľte prístup k polohe v prehliadači.'),
        })
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }
  /**
   * The preselected default address, resolved - §9.371, FB-659.
   *
   * **`customerAddress` is `typedAddress ?? knownAddress`**, so a returning customer arrives with
   * `defaultAddress(saved)` already in the field and **no `point`** behind it - and `zoneFor` needs
   * the point, so the fee could not be quoted and the checkout asked for a zone. The owner's report:
   * *"when I go checkout in customer and I have preselected or select some, it is not actually
   * verified, and asking for Doručovacia zóna."*
   *
   * §9.52 fixed the **click** on a saved chip - *"chosen means resolved"* - and this is the same
   * defect one path over, on the commonest case of all.
   *
   * **Each guard is a bug it would otherwise be.** Never when a point exists: a resolved point is
   * better than a fresh lookup and overwriting it would move a quote somebody already saw. Never
   * once they have typed: from then on the field owns the address and its own `onResolved` answers
   * for it. Never on a blank. And a failed lookup leaves the point alone, which is §9.52's rule.
   */
  useEffect(() => {
    const address = addressToResolve({ point, typedAddress, knownAddress })
    if (address === null) return
    let cancelled = false
    void geocodeOnce(address).then((place) => {
      if (!cancelled && place !== null) setPoint({ lat: place.lat, lng: place.lon })
    })
    return () => {
      cancelled = true
    }
  }, [knownAddress, typedAddress, point])

  useEffect(() => {
    // No synchronous `setState` here: the reset is *derived* at render instead. Setting state on the
    // way out of an effect is a cascading render, and the linter is right to refuse it.
    const address = customerEmail.trim()
    if (!joinLoyaltyOffered || !address.includes('@')) return
    let cancelled = false
    const timer = setTimeout(() => {
      void getCustomersLoyaltyCheck({ email: address })
        .then((answer) => {
          // **One field, and that is the whole answer.** BLOCKED #11 / §10A.8: this endpoint used to
          // hand a stranger the owner's name, order count, tier and balance for any address they
          // could guess. It says `found` now and nothing else, so this panel can say an account
          // exists and cannot say whose — which is the correct amount for somebody who has proved
          // only that they can type.
          if (!cancelled) setExistingAccount((answer as { found?: boolean }).found === true)
        })
        .catch(() => {
          if (!cancelled) setExistingAccount(false)
        })
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [customerEmail, joinLoyaltyOffered])
  // Shut, and no pre-order to sidestep it with. Legacy's `orderingBlocked`, by another name.
  /**
   * §9.219 — the default, applied once per day the basket asks for.
   *
   * A default and not a lock: switching it off afterwards sticks, because `lunchDayHandled` has
   * already recorded that this day was offered. Set during render rather than in an effect — an
   * effect calling `setPreorder` would fight the customer for one frame every time.
   */
  if (lunchDay !== null && lunchDay !== lunchDayHandled) {
    setLunchDayHandled(lunchDay)
    setPreorder({ date: lunchDay, time: lunchWindow?.from ?? '' })
  }

  const closed = !mayOrder(state) && preorder === null
  const scheduling = preorder === null ? '' : preorderProblem(preorder.date, preorder.time, hours, new Date())
  /**
   * The chosen slot, in the words the manager's časenka already uses — §9.18.
   *
   * `null` until both halves are set, because *sobota 2. 08. 00:00* from a half-filled form reads as a
   * decision somebody made rather than one they have not made yet.
   */
  const scheduledSummary = useMemo(() => {
    if (preorder === null || preorder.date === '' || preorder.time === '') return null
    const scheduledAt = scheduledAtOf(preorder.date, preorder.time)
    return scheduledAt === null ? null : formatNamedDayTime(scheduledAt)
  }, [preorder])
  const facts: CheckoutFacts = {
    type,
    customerName,
    customerPhone,
    customerEmail,
    customerAddress,
    point,
    paymentMethod,
    notes,
    deliveryCents,
    couponCode: liveCoupon?.code ?? null,
    referralCode: referral,
    redeemedPoints: Math.min(redeem, redeemable),
    preorder,
  }
  const problems = submitProblems(facts, priced)

  // A preorder is allowed while the restaurant is shut — that is what it is for. An immediate order is
  // not, and `openState` answering `unknown` counts as not (FB-C06).
  const maySubmit =
    problems.length === 0 &&
    scheduling === '' &&
    (preorder !== null || mayOrder(state)) &&
    delivery.kind !== 'below-minimum' &&
    (!needsAddress || delivery.kind === 'ok' || delivery.kind === 'none-configured') &&
    !placeOrder.isPending

  /** Answers whether the code was accepted, so §9.71's single box can fall through to the referral. */
  async function applyCoupon(typed: string): Promise<boolean> {
    const code = typed.trim().toUpperCase()
    if (code === '') return false
    setCouponError('')
    try {
      const answer = await validateCoupon.mutateAsync({
        data: {
          code,
          orderAmount: subtotal / 100,
          customerEmail: customerEmail.trim() === '' ? undefined : customerEmail.trim(),
          cartProductIds: couponLines.map((entry) => entry.productId),
        },
      })
      const applied = couponFrom(answer)
      if (applied === null) {
        setCouponError(couponRefusal(answer) ?? tt('Kupón sa nepodarilo uplatniť.'))
        return false
      }
      setCoupon(applied)
      return true
    } catch (failure) {
      // The server's own wording, which is written for the customer.
      const body = (failure as { body?: unknown }).body
      setCouponError(couponRefusal(body) ?? tt('Kupón sa nepodarilo uplatniť.'))
      return false
    }
  }

  /**
   * One box, both codes — §9.71, owner decision 2026-08-06.
   *
   * Legacy has a single field and AGOS split it, which is the difference the owner reported. The
   * split existed for a real reason: the two endpoints refuse for different reasons, and collapsing
   * them to *neplatný kód* throws away the only useful half of a refusal.
   *
   * So the code is tried as a coupon, then as a referral, and **a refusal from both says both were
   * tried**. That is the sentence legacy's single box cannot produce.
   *
   * Coupon first because it is the common case, and because its endpoint answers the discount the
   * preview must not invent. A referral code reaching it first costs one refused validation.
   */
  async function applyCode() {
    const typed = codeInput.trim()
    if (typed === '') return
    setCodeError('')

    const couponWorked = await applyCoupon(typed.toUpperCase())
    if (couponWorked) {
      setCodeInput('')
      return
    }
    const referralWorked = await applyReferral(typed)
    if (referralWorked) {
      setCodeInput('')
      return
    }
    setCodeError(
      `${tt('Kód')} ${typed} ${tt('neplatí ako zľavový kupón ani ako odporúčací kód.')}`,
    )
  }

  /**
   * Check a referral code before the order carries it.
   *
   * Checked here rather than at submit because a code that will be refused should be refused while
   * there is still a form to correct it on — legacy validates at submit and the customer finds out by
   * having their order rejected.
   *
   * The **e-mail goes with the code**: the server refuses a customer referring themselves, and it needs
   * to know who is asking to do that.
   */
  async function applyReferral(typed: string): Promise<boolean> {
    setReferralProblem('')
    const code = typed.trim()
    if (code === '') return false
    try {
      // The server answers the discount it will apply; the preview must not invent one.
      const answer = (await validateReferral.mutateAsync({
        data: { code, email: customerEmail.trim() },
      })) as { discountType?: unknown; discountValue?: unknown } | null
      setReferralCents(referralDiscountCents(answer, subtotal))
      setReferral(code)
      return true
    } catch (failure) {
      setReferralProblem(apiErrorField(failure, 'error') ?? tt('Kód sa nepodarilo overiť.'))
      return false
    }
  }

  async function submit() {
    setSubmitError('')
    try {
      const order = await placeOrder.mutateAsync({
        data: orderPayload(facts, priced, [
          ...lunchOrderLines(lunchBasket, lunchBundlePrice),
          ...composedLines.map((line) => ({
            productId: line.productId,
            quantity: line.quantity,
            unitPriceCents: line.composed?.unitPriceCents ?? 0,
            customName: line.composed?.name ?? '',
            // A gift travels the same road — it has no catalogue price either — but it must arrive with
            // its rule id, or the server prices it as a product and the customer pays for their gift.
            giftRuleId: line.giftRuleId,
          })),
        ]) as unknown as CreateOrderRequest,
      })
      const placed = order as { id?: number; orderNumber?: string } | null
      clear()

      // After the order, and never before it: joining is a favour the customer is doing the restaurant,
      // and a registration that failed must not take an order down with it. Legacy does the same and
      // calls it non-fatal, which is right.
      if (joinLoyalty && customerEmail.trim() !== '') {
        await joinProgramme
          .mutateAsync({
            data: {
              email: customerEmail.trim(),
              name: customerName.trim(),
              phone: customerPhone.trim(),
              isLoyalty: true,
            },
          })
          .catch(() => undefined)
      }

      // The landing paths carry no order reference — `Landing` is two bare paths — so what the success
      // page will be able to say is left here, in this tab, before the browser goes anywhere.
      rememberOrder({
        points: null,
        orderId: placed?.id ?? null,
        orderNumber: placed?.orderNumber ?? null,
      })

      if (paymentMethod === 'card_online' && placed?.id !== undefined) {
        // The amount is checked against the order's own total server-side, so this is a request rather
        // than an instruction.
        const session = await initiatePayment.mutateAsync({
          data: {
            orderId: placed.id,
            orderNumber: placed.orderNumber ?? '',
            amount: centsToEuros(total),
          },
        })
        const redirect = (session as { redirectUrl?: string } | null)?.redirectUrl
        if (typeof redirect === 'string' && redirect !== '') {
          window.location.assign(redirect)
          return
        }
        // The order exists and is unpaid, which is exactly what the payment-error page is for. Saying
        // nothing and staying here would leave a placed order looking like a failed submission.
        navigate('/payment-error')
        return
      }
      navigate('/success')
    } catch (failure) {
      setSubmitError(apiErrorField(failure, 'error') ?? tt('Objednávku sa nepodarilo odoslať.'))
    }
  }

  // An empty basket has nothing to check out, and legacy sends the customer back to the menu rather
  // than showing them a form with nothing in it. Rendering as a redirect rather than firing one in an
  // effect: the effect version paints the empty page for a frame first.
  //
  // **Both halves count.** A basket of nothing but daily menu resolves no catalogue lines at all —
  // lunch meals are not in `/products` — so checking `priced` alone would turn a customer with two
  // soups and a burger straight back around at the door.
  //
  // **And the catalogue has to have answered.** `priced` is the basket resolved against `/products`,
  // which is empty on the first render, so this used to redirect *before the products arrived*: anyone
  // arriving at the checkout without the catalogue already in the query cache — a refresh, a bookmark,
  // a link, a return from the bank — was bounced to the menu with a full basket. The flows never saw
  // it because they walk in from the menu, where the catalogue is already cached. FB-C52.
  const emptyBasket = lines.length === 0 && lunchBasket.length === 0 && composedLines.length === 0
  const resolvesToNothing = priced.length === 0 && lunchBasket.length === 0 && composedLines.length === 0

  // **And the same for the lunch menu, which FB-C52 fixed for the catalogue only.** A basket holding
  // nothing but a daily menu resolves to nothing until `lunchData` arrives, so the guard bounced it
  // exactly as it used to bounce a full one. It never showed while the menu page fetched lunch under
  // the same query key — the checkout always found it warm in the cache. §10A.48 gave that page a
  // `from` parameter, the keys stopped matching, and a lunch-only basket started redirecting on a
  // cold cache: a refresh, a bookmark, or a return from the bank.
  if (emptyBasket || (resolvesToNothing && !productsLoading && !lunchLoading)) {
    return <Redirect to="/" />
  }

  if (resolvesToNothing) {
    // Lines in hand, catalogue still coming. Say nothing rather than flashing a form with no prices in
    // it; the wait is one request and the alternative is losing the basket.
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-checkout-loading>
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-8 pb-32 overflow-x-hidden" data-checkout>
      <div className="flex items-center gap-4 mb-8">
        <Link
          href="/"
          data-to-menu
          aria-label={tt('Späť na menu')}
          className="p-3 bg-card rounded-full shadow-sm hover:bg-secondary active:bg-secondary/80 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-3xl font-display font-bold">
          <T>Dokončenie objednávky</T>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
<section className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">1</span>
          <T>Spôsob doručenia</T>
        </h2>
        <div className="grid grid-cols-2 gap-4" data-order-type>
        {orderTypes.map((option) => {
          // The icon follows the capability, as `iconFor` does in the manager: one that takes an
          // address is a van, one that seats people is a restaurant, anything else is a counter. A
          // per-slug guess would break for the fifth channel exactly as the hard-coded pair did.
          const Icon = option.requiresDeliveryAddress ? Truck : option.usesTables ? UtensilsCrossed : Store
          return (
            <button
              key={option.slug}
              type="button"
              data-order-type-option={option.slug}
              aria-pressed={type === option.slug}
              onClick={() => setType(option.slug)}
              className={`cursor-pointer flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
                type === option.slug
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/30 text-muted-foreground'
              }`}
            >
              <Icon className="w-8 h-8" />
              <span className="font-semibold">
                <T>{option.name}</T>
              </span>
            </button>
          )
        })}
      </div>
      </section>

      <section className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">2</span>
          <T>Vaše údaje</T>
        </h2>
        <div className="space-y-5" data-customer-fields>
        {/* Name and phone are one row in legacy, and one row is what they read as. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <CheckoutField id="field-name" icon={User} label={tt('Meno a priezvisko *')}>
            <input
              id="field-name"
              data-field="name"
              autoComplete="name"
              value={customerName}
              onChange={(event) => setName(event.target.value)}
              placeholder={tt('Napr. Jozef Mrkvička')}
              className={FIELD_CLASS}
            />
          </CheckoutField>
          <CheckoutField id="field-phone" icon={Phone} label={tt('Telefónne číslo *')}>
            <input
              id="field-phone"
              data-field="phone"
              type="text"
              inputMode="tel"
              autoComplete="tel"
              value={customerPhone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="0912 345 678"
              className={FIELD_CLASS}
            />
          </CheckoutField>
        </div>
        <CheckoutField
          id="field-email"
          icon={Mail}
          // Legacy marks it required only while the programme is on offer, and says *(voliteľné)*
          // otherwise rather than leaving the field unexplained.
          label={joinLoyaltyOffered ? tt('E-mail *') : `${tt('E-mail')} ${tt('(voliteľné)')}`}
          // Legacy explains the e-mail rather than just asking for it, and only while the programme is
          // on offer — an address collected for nothing needs no justification.
          hint={joinLoyaltyOffered ? tt('Email je potrebný pre vernostný program. Môžete ho zadať aj bez registrácie do programu.') : undefined}
        >
          <input
            id="field-email"
            data-field="email"
            type="email"
            autoComplete="email"
            value={customerEmail}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="vas@email.sk"
            className={FIELD_CLASS}
          />
        </CheckoutField>

        {/* §9.163, the owner's point 46 - *"zapojte sa do vernostného programu should be under the
          * E-mail"*. It was a card of its own further down the page, which put the tick a scroll away
          * from the address it depends on: the field's own hint already says *"Email je potrebný pre
          * vernostný program"*, and the thing that sentence refers to was nowhere near it. */}
        {/* §9.163 - legacy's panel without the name it prints. A prompt, not a gate: ordering as a
          * guest is still allowed, but the points this address is owed need a sign-in, and being told
          * that before the order beats discovering it after. */}
        {/* §9.218 - the third state, and legacy's card: signed in and in the programme. The two
          * below it are for a guest, so all three are mutually exclusive by construction — nobody
          * signed in is offered a tick to join what they are already in. */}
        {user !== null && rates !== null && rates.isActive && (
          <div
            className="bg-primary/8 border border-primary/25 rounded-2xl px-4 py-3 space-y-3"
            data-loyalty-greeting
          >
            <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
              ★
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold truncate">
                <T>Vítajte späť</T>
                {knownName === '' ? '' : `, ${knownName.split(' ')[0] ?? ''}`}
              </span>
              <span className="block text-xs text-muted-foreground">
                {memberLevel === '' ? (
                  <T>Vernostný člen</T>
                ) : (
                  <>
                    <T>Vernostný člen</T>
                    {' · '}
                    {/* §9.250 - named, from `tierFor`, which is the reader `ProfilePage` uses. The
                      * comment here used to refuse a name because the thresholds carry none and two
                      * screens inventing their own would disagree; §9.246 gave both the franchise's
                      * own `name`, `emoji` and `color`, so sharing the reader is what keeps them
                      * agreeing. */}
                    {/* §9.279, owner O27 - *"for naming use FARBA from loyalty… these emojis set, and
                      * also názov… in profile, and also in checkout."* The profile has styled this
                      * badge with the tier's own colour since §9.246 (`ProfilePage.tsx:775`) and the
                      * checkout printed it in the surrounding text colour, so one screen showed the
                      * franchise's palette and the other did not. Same three values, same treatment. */}
                    <span
                      className={memberTier === null ? undefined : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border'}
                      style={
                        memberTier === null
                          ? undefined
                          : {
                              background: `${memberTier.color}1A`,
                              color: memberTier.color,
                              borderColor: `${memberTier.color}55`,
                            }
                      }
                      data-loyalty-level={memberLevel}
                      data-loyalty-tier-name={memberTier?.name ?? memberLevel}
                      data-loyalty-tier-colour={memberTier?.color ?? ''}
                    >
                      {memberTier === null ? memberLevel : `${memberTier.emoji} ${memberTier.name}`}
                    </span>
                  </>
                )}
              </span>
            </span>
            <span className="text-right shrink-0">
              <span className="block text-[11px] text-muted-foreground">
                <T>Zostatok bodov</T>
              </span>
              {/* Said even at nought: the redemption control below is right to stay quiet when there
                * is nothing to spend, but *you have 0* is the fact somebody opens this looking for. */}
              <span className="block text-sm font-bold text-primary" data-loyalty-balance={balance}>
                {formatCount(balance)} <T>b.</T>
              </span>
            </span>
            </div>

            {/* §9.278, owner O23 - what this order earns, said beside the tier that decides it and
              * only above the ordinary rate. It belongs here rather than in the redemption panel:
              * this is about what the order *earns*, not what it spends. */}
            {multiplierNote(memberTier?.name ?? null, memberTier?.multiplier ?? 1) !== '' && (
              <p className="text-[11px] text-primary font-semibold pt-1" data-points-multiplier>
                {multiplierNote(memberTier?.name ?? null, memberTier?.multiplier ?? 1)}
              </p>
            )}

            {/* §9.250 - the owner's N9. Legacy keeps the redemption inside this same box
              * (`Checkout.tsx:1556`–`:1780`); AGOS had it two cards further down, behind the address
              * form, under a second *Vernostné body* heading. Moved, not rebuilt: same slider, same
              * hooks, same arithmetic. */}
            {balance > 0 && (
              <div className="border-t border-primary/20 pt-3 space-y-2" data-redeem>
                {redeemNote !== '' ? (
                  <p className="text-xs text-muted-foreground" data-redeem-note>
                    {redeemNote}
                  </p>
                ) : (
                  <>
                    {/* §9.278, owner O23 - legacy's panel. The heading and the number box are here
                      * from the moment the card opens; the owner's complaint was that *Koľko bodov
                      * uplatniť?* only appeared after pressing a button. */}
                    <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                      <T>Koľko bodov uplatniť?</T>
                    </p>

                    {/* Legacy's two tiles: what the basket is, and what it becomes. */}
                    <div className="grid grid-cols-2 gap-2 text-xs" data-redeem-preview>
                      <div className="rounded-lg bg-background border border-border px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                          <T>Suma košíka</T>
                        </p>
                        <p className="font-bold tabular-nums">{formatCents(absorbableCents)}</p>
                      </div>
                      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-bold">
                          <T>Po uplatnení</T>
                        </p>
                        <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-400" data-redeem-after>
                          {formatCents(Math.max(0, absorbableCents - redeemedCents))}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        data-redeem-amount
                        aria-label={tt('Koľko bodov uplatniť?')}
                        min={0}
                        max={redeemable}
                        step={1}
                        value={Math.min(redeem, redeemable)}
                        onChange={(event) => setRedeem(Number(event.target.value) || 0)}
                        className="w-24 px-2 py-1.5 rounded-lg border border-border bg-background text-right text-sm font-bold tabular-nums"
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        <T>z</T> {formatCount(balance)} <T>bodov</T>
                      </span>
                      <div className="flex-1" />
                      <span className="text-sm font-bold whitespace-nowrap" data-redeem-value>
                        −{formatCents(redeemedCents)}
                      </span>
                    </div>

                    <input
                      type="range"
                      data-redeem-slider
                      aria-label={tt('Uplatniť vernostné body')}
                      min={0}
                      max={redeemable}
                      step={1}
                      value={Math.min(redeem, redeemable)}
                      onChange={(event) => setRedeem(Number(event.target.value))}
                      className="w-full accent-primary"
                    />

                    {/* The six legacy offers, with the ones the ceiling cannot reach dropped. */}
                    <div className="flex items-center gap-1.5 flex-wrap" data-redeem-quick>
                      {quickAmounts(rates, redeemable).map((amount: QuickAmount) => (
                        <button
                          key={amount.points}
                          type="button"
                          data-redeem-quick-amount={amount.points}
                          onClick={() => setRedeem(amount.points)}
                          className="px-2 py-1 rounded-md bg-muted hover:bg-muted/70 text-[11px] font-bold text-muted-foreground"
                        >
                          {amount.label}
                        </button>
                      ))}
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                      {/* The balance and what it is worth are different facts, and legacy shows
                        * both. */}
                      <T>Zostatok</T>: {balanceNote(rates, balance)}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {existingAccount && joinLoyaltyOffered && customerEmail.includes('@') && (
          <div
            className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl px-4 py-3"
            data-account-exists
          >
            <User className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-bold">
                <T>Účet pre tento e-mail existuje</T>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                <T>
                  Pre vernostné body, históriu objednávok a osobnú zľavu sa najprv prihláste.
                </T>
              </p>
            </div>
          </div>
        )}

        {joinLoyaltyOffered && !(existingAccount && customerEmail.includes('@')) && (
          <label className="flex items-start gap-3 cursor-pointer -mt-1">
            <input
              type="checkbox"
              data-join-loyalty
              checked={joinLoyalty}
              onChange={(event) => setJoinLoyalty(event.target.checked)}
              className="w-5 h-5 rounded accent-primary mt-0.5 shrink-0"
            />
            <span>
              <span className="block text-sm font-bold">
                <T>Zapojte sa do vernostného programu</T>
              </span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                <T>Za objednávky získavate body, ktoré si môžete uplatniť pri ďalšej objednávke.</T>
              </span>
            </span>
          </label>
        )}

        {needsAddress && saved.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-saved-addresses>
            {saved.map((entry) => (
              <button
                key={entry.address}
                type="button"
                data-saved-address={entry.address}
                aria-pressed={customerAddress === entry.address}
                onClick={() => {
                  // §9.52 — chosen means resolved. This set the text and nothing else, so the one
                  // customer whose address the restaurant already knows was the one who could not be
                  // quoted a fee. A failed lookup leaves the previous point alone rather than
                  // clearing a good quote.
                  setAddress(entry.address)
                  void geocodeOnce(entry.address).then((place) => {
                    if (place !== null) setPoint({ lat: place.lat, lng: place.lon })
                  })
                }}
                className={`min-h-[36px] px-3 rounded-xl border text-xs font-semibold transition-colors ${
                  customerAddress === entry.address
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                {/* §9.332 - the owner: *"add dropdown for these addresses, so I can actually see
                    that Address."* This read `entry.label ?? entry.address`, so an address the
                    customer had named showed **only the name**: *Domov*, with the street nowhere on
                    the screen. Both now, label first - the label is how they recognise it and the
                    address is what they are checking before they order to it. */}
                {addressSummary(entry)}
                {entry.isDefault && <span className="ml-1 opacity-60">★</span>}
              </button>
            ))}
          </div>
        )}
        {needsAddress && (
          <CheckoutField
            id="field-address"
            icon={MapPin}
            label={tt('Adresa doručenia *')}
            hint={tt('Funguje aj pre adresy, ktoré Google nepozná')}
          >
            {/* The field said *Začnite písať adresu…* and *funguje aj pre adresy, ktoré Google
                nepozná* while offering no suggestions at all - the screen was built expecting an
                autocomplete that was parked on a missing Maps key. The key arrived 2026-08-03
                (BLOCKED #7); this connects what the copy already promised.
                A delivery address a customer types is where a wrong one costs a delivery, so this
                is the highest-volume of the three surfaces the component now serves.
                `id` and `data-field` are preserved because five e2e assertions find the field by
                them, and the native `street-address` autofill stays: a browser that already knows
                the customer's address should still offer it, ahead of anything Google says. */}
            <AddressField
              value={customerAddress}
              onChange={setAddress}
              // §9.51 — the point the field already resolved. Without this the checkout threw it
              // away and `point` was only ever set by *use my location*, so every typed address was
              // refused however central it was. `lon` is Google's spelling; `zoneFor` takes `lng`,
              // and reading the wrong one is `undefined` → `NaN` → outside every radius.
              onResolved={(place) => setPoint({ lat: place.lat, lng: place.lon })}
              // §9.372: an unresolved pick must not leave the previous address's coordinates
              // behind the new text - the fee and the zone are both read off `point`.
              onUnresolved={() => setPoint(null)}
              ariaLabel={tt('Adresa doručenia')}
              placeholder={tt('Začnite písať adresu...')}
              inputProps={
                {
                  id: 'field-address',
                  'data-field': 'address',
                  autoComplete: 'street-address',
                  className: FIELD_CLASS,
                } as InputHTMLAttributes<HTMLInputElement>
              }
              trailing={
                <button
                  type="button"
                  data-locate
                  onClick={locate}
                  disabled={isLocating}
                  aria-label={tt('Použiť moju polohu')}
                  title={tt('Funguje aj pre adresy, ktoré Google nepozná')}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
                >
                  {/* §9.68's accessible name survives the move - an icon-only button is only as
                    * good as what it announces. The title is legacy's, and it is the sentence that
                    * explains why anyone would press this instead of typing. */}
                  {isLocating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <LocateFixed className="w-4 h-4" />
                  )}
                </button>
              }
            />
          </CheckoutField>
        )}
        <CheckoutField id="field-notes" icon={StickyNote} label={tt('Poznámka k objednávke')}>
          <textarea
            id="field-notes"
            data-field="notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={tt('Napr. zvonček nefunguje, volať...')}
            className={`${FIELD_CLASS} min-h-[80px]`}
          />
        </CheckoutField>
      </div>

      {/* Legacy has no separate delivery card: the address, the locate button and what the zone
          charges all sit in *Vaše údaje*, because they are one answer to one question. */}
      {/* §9.68 - one row, not two. The locate control shrank to an icon so it could sit beside what
          it produces: a full-width *Použiť moju polohu* took a row of its own directly above the
          line naming the zone it resolves, which is the pair the owner was reading. */}
      {/* §9.164 - the button moved into the address box, where the owner asked for it. What is left
          here is the zone line, which keeps `data-delivery`. */}
      {needsAddress && (
        <div className="flex items-center gap-2" data-delivery>
            {delivery.kind === 'ok' ? (
              // §9.67 — the zone, the fee, and the minimum. The minimum is the one number here
              // that can stop an order, and it was the one this line did not say.
              <p className="text-xs" data-delivery-zone={delivery.zone.name}>
                {zoneLine(delivery.zone, delivery.feeCents)}
              </p>
            ) : (
              // §9.51 — nothing at all until there is something to say. `unknown` has no note, and an
              // empty amber paragraph is still a paragraph: it reserves space and reads as a warning
              // that failed to load.
              deliveryNote(delivery) !== '' && (
                <p className="text-xs text-amber-500" data-delivery-note={delivery.kind}>
                  {deliveryNote(delivery)}
                </p>
              )
            )}
            {/* §9.64 - what the basket is still short of, and only when there is something to
              * reach. Below the zone's minimum comes first: that is a gate, not a saving, and no
              * tier matters while the order cannot be placed at all. */}
            {(() => {
              const progress = basketProgress(delivery, subtotal)
              if (progress === null) return null
              return (
                <div className="space-y-1" data-basket-progress={progress.kind}>
                  <p className="text-[11px] text-muted-foreground">
                    {progress.kind === 'minimum' ? (
                      <>
                        <T>Do minimálnej objednávky chýba</T> {formatCents(progress.missingCents)}
                      </>
                    ) : progress.free ? (
                      <>
                        <T>Do dopravy zadarmo chýba</T> {formatCents(progress.missingCents)}
                      </>
                    ) : (
                      <>
                        <T>Ešte</T> {formatCents(progress.missingCents)}{' '}
                        <T>a doprava bude</T> {formatCents(progress.feeCents)}
                      </>
                    )}
                  </p>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${Math.round(progressFraction(progress) * 100)}%` }}
                    />
                  </div>
                </div>
              )
            })()}
        </div>
      )}
      </section>



      {/* Joining is offered to a guest, once, at the moment they are already typing their details.
          Not offered to somebody signed in - they are in it. */}
      <section className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">3</span>
          <T>Spôsob platby</T>
        </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-payment>
        {methods.map((method) => {
          const chosen = paymentMethod === method
          // Legacy tints the online option violet rather than primary — it is the one that leaves the
          // site for a bank, and it looks different because it behaves differently.
          const online = method === 'card_online'
          const frame = chosen
            ? online
              ? 'border-violet-500 bg-violet-50 text-violet-700'
              : 'border-primary bg-primary/5 text-primary'
            : online
              ? 'border-border hover:border-violet-300 text-foreground'
              : 'border-border hover:border-primary/30 text-foreground'
          const tile = chosen
            ? online
              ? 'bg-violet-500 text-white'
              : 'bg-primary text-white'
            : 'bg-secondary text-muted-foreground'
          return (
            <button
              key={method}
              type="button"
              data-payment-option={method}
              aria-pressed={chosen}
              onClick={() => setChosenPayment(method)}
              className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${frame}`}
            >
              <div className={`p-2 rounded-xl shrink-0 ${tile}`}>
                {method === 'cash' ? <Banknote className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <div className="font-bold">{paymentMethodLabel(method)}</div>
                <div className="text-xs opacity-70">{paymentMethodNote(method)}</div>
              </div>
            </button>
          )
        })}
      </div>
      </section>
      </div>

      {/* The summary column, which legacy keeps in view while the form is filled. */}
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-24 space-y-4">
      <div className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-5" data-summary>
        {/* What is being bought, which this card did not say at all - FB-C53. Legacy heads the summary
            with the order and lets it be changed here, because the checkout is the last place anybody
            reads it back before paying, and going to the menu to fix a quantity loses the form. */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-display font-bold">
            <T>Vaša objednávka</T>
          </h2>
          <button
            type="button"
            data-clear-cart
            onClick={clear}
            className="text-xs text-destructive hover:text-destructive/80 font-medium transition-colors"
          >
            <T>Vyprázdniť košík</T>
          </button>
        </div>

        <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
          {/* §9.166 - a box is one entry. Its lines have carried `boxGroupId` since the box was
              built; this screen was the one place that never read it, so a box of three arrived as
              three rows with three steppers, any of which could take it apart. */}
          {groupBasket(priced).map((group) =>
            group.kind === 'box' ? (
              <div
                key={group.groupId}
                data-checkout-box={group.groupId}
                className="border border-amber-200 rounded-2xl overflow-hidden bg-amber-50/50"
              >
                <div className="flex items-center justify-between px-3 py-2 bg-amber-100/70 border-b border-amber-200">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Package className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span className="text-xs font-black text-amber-700 uppercase tracking-wide truncate">
                      {group.name}
                    </span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold text-amber-700" data-box-total>
                      {formatCents(group.totalCents)}
                    </span>
                    {/* One bin for the whole box, and no stepper: half a box is not something the
                        kitchen makes. `removeBoxGroup` is what the cart wrote for this. */}
                    <button
                      type="button"
                      data-box-remove
                      aria-label={tt('Odobrať z košíka')}
                      onClick={() => removeBoxGroup(group.groupId)}
                      className="w-6 h-6 rounded-full bg-white text-destructive flex items-center justify-center shadow-sm hover:bg-destructive hover:text-white transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <div className="divide-y divide-amber-100 px-3">
                  {group.items.map((item) => (
                    <div key={item.key} className="flex items-center gap-2 py-1.5" data-box-item>
                      {item.line.boxSlotLabel !== undefined && (
                        <span className="text-xs font-bold text-amber-500 uppercase w-16 shrink-0 truncate">
                          {item.line.boxSlotLabel}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-foreground flex-1 min-w-0 truncate">
                        {item.name}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatCents(item.unitCents)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <BasketLine
                key={group.entry.key}
                entry={group.entry}
                onRemove={removeLine}
                onChangeBy={changeBy}
              />
            ),
          )}

          {/* §9.173 / §9.185 - the day's menu, in a box like the burger box, and removed as one.
              It is listed and totalled but not stepped: a soup's price changes when the main beside
              it leaves, which is `repriceLunchSoups`. That is also the owner's reason for one bin
              rather than a bin per meal - taking half a set out silently reprices the other half. */}
          {lunchBasket.length > 0 && (
            <div
              className="border border-amber-200 rounded-2xl overflow-hidden bg-amber-50/50"
              data-checkout-lunch-box
            >
              <div className="flex items-center justify-between px-3 py-2 bg-amber-100/70 border-b border-amber-200">
                <span className="flex items-center gap-1.5 min-w-0">
                  <Soup className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="text-xs font-black text-amber-700 uppercase tracking-wide truncate">
                    <T>Denné menu</T>
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-bold text-amber-700" data-lunch-box-total>
                    {formatCents(lunchCents)}
                  </span>
                  <button
                    type="button"
                    data-lunch-remove
                    aria-label={tt('Odobrať denné menu')}
                    onClick={() => {
                      for (const meal of lunchBasket) removeLine(lineKey(lunchLineOf(meal.meal)))
                    }}
                    className="w-6 h-6 rounded-full bg-white text-destructive flex items-center justify-center shadow-sm hover:bg-destructive hover:text-white transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              </div>
              <div className="divide-y divide-amber-100 px-3">
                {lunchBasket.map((meal, index) => (
                  <div
                    key={`lunch-${index}`}
                    className="flex items-center gap-2 py-1.5"
                    data-checkout-lunch-line
                  >
                    <span className="text-sm font-semibold text-foreground flex-1 min-w-0 truncate">
                      {meal.quantity > 1 && `${formatCount(meal.quantity)}× `}
                      {meal.meal.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {composedLines.map((line) => (
            <div
              key={lineKey(line)}
              className="flex gap-4 items-center"
              data-checkout-composed-line={line.quantity}
            >
              <div className="flex-1 min-w-0">
                {/* §9.245 - §9.172's count, on the list it never reached. Five gifts read as one
                    thing, and a customer reads their order back off the list. */}
                <div className="font-bold">
                  {line.quantity > 1 && `${formatCount(line.quantity)}× `}
                  {line.composed?.name}
                </div>
                {line.giftRuleId !== undefined && (
                  <div
                    className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400"
                    data-line-gift={line.giftRuleId}
                  >
                    🎁 <T>Darček</T>
                  </div>
                )}
                <div className="text-primary font-bold text-sm">
                  {formatCents((line.composed?.unitPriceCents ?? 0) * line.quantity)}
                </div>
              </div>
              <LineStepper
                quantity={line.quantity}
                isGift={line.giftRuleId !== undefined}
                onRemove={() => removeLine(lineKey(line))}
                onChangeBy={(delta) => changeBy(lineKey(line), delta)}
              />
            </div>
          ))}

          {/* §9.142's reasoning, §9.167's placement. Legacy evaluates the gift rule on its checkout
              as well as its menu, and a customer who crossed the threshold and came straight here
              would otherwise never be told. Re-evaluated rather than remembered: this is what the
              basket earns *now*, which is also right after a line was removed on the way. A card in
              the order and not a dialog - a popup over somebody entering an address is an
              interruption, and leaving it alone is the refusal.

              In the order list rather than among the totals, because a gift is a thing you are
              getting and not a subtraction from what you owe. */}
          {checkoutGifts.map((offer) => (
            <div
              key={offer.ruleId}
              data-checkout-gift={offer.ruleId}
              className="mt-2 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/50 dark:bg-red-950/30"
            >
              <span className="text-2xl shrink-0" aria-hidden="true">
                🎁
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
                  <T>Darček k objednávke</T>
                </p>
                <p className="text-sm font-semibold text-red-800 dark:text-red-200 truncate">
                  {/* The count and the price, because legacy shows both. *2× Coca-Cola* is a
                      different offer from *Coca-Cola*, and a price that is not zero is what says
                      the gift is discounted rather than free. */}
                  {offer.count > 1 && `${formatCount(offer.count)}× `}
                  {offer.giftName}
                  {offer.giftPriceCents > 0 && ` – ${formatCents(offer.giftPriceCents)}`}
                </p>
              </div>
              <button
                type="button"
                data-checkout-gift-accept
                onClick={() => add(giftLine(offer))}
                className="shrink-0 rounded-xl bg-red-600 hover:bg-red-700 active:scale-95 px-4 py-2 text-xs font-bold text-white transition-all"
              >
                <T>Pridať darček</T>
              </button>
            </div>
          ))}
        </div>


        <div className="pt-5 border-t border-border border-dashed space-y-4">
          {/* Legacy keeps the discount code beside the total it changes rather than in a numbered
              section down the page - a coupon is read against a price. Its referral field is the same
              box in legacy; kept separate here because the two endpoints validate differently. */}
            {exclusive ? (
              <p className="text-xs text-muted-foreground" data-coupon-replaced>
                {replacedNote()}
              </p>
            ) : (
              <>
                {/* §9.71 - one box, both codes tried. Legacy has a single field; the two AGOS had
                  * were the difference the owner reported. `applyCode` tries the coupon endpoint and
                  * then the referral one, so a refusal from both can say both were tried — which is
                  * the sentence legacy's single box cannot produce. */}
                <div className="flex gap-2">
                  <input
                    data-coupon-input
                    value={codeInput}
                    onChange={(event) => setCodeInput(event.target.value)}
                    placeholder={tt('Zľavový alebo odporúčací kód')}
                    className="flex-1 min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm"
                  />
                  <button
                    type="button"
                    data-coupon-apply
                    onClick={() => void applyCode()}
                    disabled={
                      codeInput.trim() === '' ||
                      validateCoupon.isPending ||
                      validateReferral.isPending
                    }
                    className="min-h-[40px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-50"
                  >
                    <T>Uplatniť</T>
                  </button>
                </div>
                {codeError !== '' && (
                  <>
                    <p className="text-xs text-red-500" data-code-error>
                      {codeError}
                    </p>
                    {/* The specific refusal, kept. "Both were tried" is the merge; *why* each said
                      * no is the thing the split was protecting, and an expired coupon is a real
                      * coupon — telling that customer only that their code is neither would be a
                      * worse answer than the two boxes gave. */}
                    {couponError !== '' && (
                      <p className="text-[11px] text-muted-foreground" data-coupon-error>
                        {couponError}
                      </p>
                    )}
                    {referralProblem !== '' && referralProblem !== couponError && (
                      <p className="text-[11px] text-muted-foreground" data-referral-problem>
                        {referralProblem}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
            {liveCoupon !== null && (
              <p className="text-xs text-emerald-500" data-coupon-applied={liveCoupon.code}>
                {liveCoupon.code} - {liveCoupon.label}
              </p>
            )}
            {referral !== null && (
              <p className="text-xs text-emerald-600" data-referral-applied={referral}>
                {referral} - <T>uplatnené</T>
              </p>
            )}

                    {/* §9.18 - the control legacy draws, not a bare checkbox.
              A full-width toggle card, the same shape as the payment and delivery choices above, so
              the three read as one set. The second line reads the chosen slot back: a customer who
              set a time three screens ago needs to see it without reopening the pickers, and that
              read-back is the part whose absence is felt more than the border. */}
            <button
              type="button"
              data-preorder-toggle
              aria-pressed={preorder !== null}
              onClick={() => setPreorder(preorder === null ? { date: '', time: '' } : null)}
              className={cn(
                'w-full flex items-center gap-4 rounded-2xl border-2 p-4 transition-all text-left',
                preorder !== null
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/30',
              )}
            >
              <div
                className={cn(
                  'p-2.5 rounded-xl shrink-0 transition-colors',
                  preorder !== null ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground',
                )}
              >
                <CalendarClock className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">
                  <T>Objednať na neskôr / Časenka</T>
                </div>
                <div className="text-xs text-muted-foreground" data-preorder-summary>
                  {scheduledSummary === null ? (
                    <T>Zvoľte termín doručenia vopred</T>
                  ) : (
                    scheduledSummary
                  )}
                </div>
              </div>
              <div
                className={cn(
                  'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                  preorder !== null ? 'bg-primary border-primary' : 'border-border',
                )}
              >
                {preorder !== null && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </div>
            </button>
            {preorder !== null && (
              <div className="flex gap-2">
                {/* §9.219 - pinned to the day the basket's daily menu is for, and bounded by the
                  * hours that menu is served: a Časenka for 21:00 on a menu served 11:00–14:00 is an
                  * order the kitchen will not honour. Unconstrained for any other basket. */}
                <input
                  type="date"
                  data-preorder-date
                  value={preorder.date}
                  min={lunchDay ?? undefined}
                  max={lunchDay ?? undefined}
                  readOnly={lunchDay !== null}
                  onChange={(event) => setPreorder({ ...preorder, date: event.target.value })}
                  className="flex-1 min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm read-only:opacity-70"
                />
                <input
                  type="time"
                  data-preorder-time
                  value={preorder.time}
                  min={lunchDay !== null ? lunchWindow?.from : undefined}
                  max={lunchDay !== null ? lunchWindow?.to : undefined}
                  onChange={(event) => setPreorder({ ...preorder, time: event.target.value })}
                  className="flex-1 min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm"
                />
              </div>
            )}
            {/* Said out loud, because a picker that refuses a date without explaining reads as
              * broken — and this one is pinned by something the customer chose two screens ago. */}
            {lunchDay !== null && (
              <p className="text-xs text-muted-foreground" data-lunch-preorder={lunchDay}>
                <T>Obedové menu je na tento deň - termín je preto pevný</T>
                {lunchWindow === null ? '' : `, ${lunchWindow.from}–${lunchWindow.to}`}.
              </p>
            )}
            {scheduling !== '' && (
              <p className="text-xs text-amber-500" data-preorder-problem>
                {scheduling}
              </p>
            )}
        </div>

      <dl className="text-sm space-y-1 pt-5 border-t border-border border-dashed">
        <div className="flex justify-between">
          <dt>
            <T>Medzisúčet</T>
          </dt>
          <dd data-subtotal>{formatCents(subtotal)}</dd>
        </div>
        {needsAddress && (
          <div className="flex justify-between">
            <dt>
              <T>Doručenie</T>
            </dt>
            <dd data-delivery-cost>
              {/* An unknown zone is **not** zero. Printing `0,00 €` beside a real total reads as free
                  delivery, which is precisely FB-188 on the manager side - and the fee here is decided
                  by an address the customer has not given yet. Legacy says so. */}
              {delivery.kind !== 'ok' && !deliveryFree ? (
                <span className="italic text-xs text-muted-foreground" data-delivery-unknown>
                  <T>Závisí od adresy</T>
                </span>
              ) : deliveryFree ? (
                <span className="flex items-center gap-1.5 justify-end">
                  <span className="line-through text-muted-foreground text-xs">
                    {formatCents(rawDeliveryCents)}
                  </span>
                  <span className="text-emerald-600 font-semibold" data-delivery-free>
                    <T>ZADARMO</T>
                  </span>
                </span>
              ) : (
                formatCents(deliveryCents)
              )}
            </dd>
          </div>
        )}
        {promoCents > 0 && (
          <div className="flex justify-between text-sm text-rose-600 font-medium" data-promo>
            <dt>{blackFridayLabel(promo)}</dt>
            <dd data-promo-cents>−{formatCents(promoCents)}</dd>
          </div>
        )}
        {/* §9.254 - legacy's own line, `Checkout.tsx:2562`. A discount that appears only as a smaller
          * number is one the customer does not know they were given, and the point of a happy hour is
          * that people come during it. */}
        {happyCents > 0 && (
          <div className="flex justify-between text-sm text-rose-600 font-medium" data-happy-hours>
            <dt className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {happyHoursLabel(happy)}
            </dt>
            <dd data-happy-hours-cents>−{formatCents(happyCents)}</dd>
          </div>
        )}
        {/* Said, not computed: the multiplier is applied by the server at order creation, and a
          * second arithmetic to keep in step would buy nothing. */}
        {happyHoursPointsNote(happy) !== '' && (
          <p className="text-[11px] text-muted-foreground" data-happy-hours-points>
            {happyHoursPointsNote(happy)}
          </p>
        )}
        {/* §9.65 - the account's own discount, named. It was always applied and always in the
          * total; one unlabelled *Zľava* line told somebody an amount came off and not that their
          * account carries a standing benefit. The two lines always sum to what was taken off,
          * because the stack is capped and a summary that does not add up is worse than one line. */}
        {(() => {
          const split = discountBreakdown(user, subtotal, discount)
          return (
            <>
              {split.personalCents > 0 && (
                <div className="flex justify-between text-emerald-500" data-personal-discount>
                  <dt>{personalDiscountLabel(user)}</dt>
                  <dd data-personal-discount-cents>−{formatCents(split.personalCents)}</dd>
                </div>
              )}
              {split.otherCents > 0 && (
                <div className="flex justify-between text-emerald-500">
                  <dt>
                    <T>Zľava</T>
                  </dt>
                  <dd data-discount>−{formatCents(split.otherCents)}</dd>
                </div>
              )}
            </>
          )
        })()}
        {/* §9.182 - its own row, not folded into the price: a deposit is not what the food costs and
            it comes back. Legacy prints it as its own receipt line for the same reason. */}
        {deposits > 0 && (
          <div className="flex justify-between">
            <dt>
              <T>Zálohy (vratné)</T>
            </dt>
            <dd data-deposits>{formatCents(deposits)}</dd>
          </div>
        )}
        <div className="flex justify-between font-bold pt-1 border-t border-border">
          <dt>
            <T>Spolu na úhradu</T>
          </dt>
          <dd data-total>{formatCents(total)}</dd>
        </div>
        {roundingNote(exact, total) !== '' && (
          <p className="text-[11px] text-muted-foreground" data-rounding-note>
            {roundingNote(exact, total)}
          </p>
        )}
      </dl>

      {/* Legacy states the refusal beside the button, in the summary, at the severity it deserves - a
          bordered destructive panel, not a pale strip at the top of the page - and offers the way
          round it. This port had the sentence but neither the placement nor the advice (FB-C53), and
          left a green *Objednať* looking merely disabled, so the reason had to be hunted for. */}
      {closed && (
        <div
          className="rounded-2xl border-2 border-destructive bg-destructive/10 px-4 py-3 flex items-start gap-3"
          data-open-state={state}
        >
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-black text-destructive text-sm">{openNote(state)}</p>
            <p className="text-xs text-destructive/90 mt-0.5">
              <T>
                Skúste to znova počas otváracích hodín alebo si vytvorte predobjednávku na neskôr
                (Časenka).
              </T>
            </p>
          </div>
        </div>
      )}

      {/* §9.243 - the operator's own notice, under the refusal rather than instead of it: one says
          the state, the other says why. Shown while open too when the owner asked for that. */}
      {announcementShows(announcement, state) && (
        <div
          className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
          data-announcement
        >
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
            {announcement.message}
          </p>
        </div>
      )}

      {submitError !== '' && (
        <p className="rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-sm" data-submit-error>
          {submitError}
        </p>
      )}

      {closed ? (
        <button
          type="button"
          data-place-order
          disabled
          className="w-full min-h-[52px] rounded-2xl bg-muted text-muted-foreground font-bold text-base cursor-not-allowed"
        >
          <T>Reštaurácia je zatvorená</T>
        </button>
      ) : (
        <button
          type="button"
          data-idempotency-key={idempotencyKey}
          data-place-order
          disabled={!maySubmit}
          onClick={() => void submit()}
          className="w-full min-h-[52px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold text-base disabled:opacity-50 transition-colors"
        >
          {placeOrder.isPending ? (
            <T>Odosielame…</T>
          ) : paymentMethod === 'card_online' ? (
            <>
              <T>Zaplatiť online</T> {formatCents(total)}
            </>
          ) : (
            <>
              <T>Záväzne objednať za</T> {formatCents(total)}
            </>
          )}
        </button>
      )}

      {/* Legacy's line under the button. A customer is agreeing to terms either way; saying so is the
          difference between a contract and a surprise. */}
      <p className="text-center text-xs text-muted-foreground">
        <T>Odoslaním súhlasíte s obchodnými podmienkami.</T>
      </p>
      </div>
        </div>
      </div>
      </div>
    </main>
  )
}

/**
 * One ordinary basket row at the checkout — extracted unchanged by §9.166 so the box branch beside it
 * reads as a branch rather than as fifty lines of JSX inside a ternary. Same markup, same attributes,
 * same handlers; only the indentation moved.
 */
/**
 * The row's quantity control — §9.245.
 *
 * **One copy, used by both lists.** It was written inside `BasketLine` and the composed list a
 * hundred lines above never got one, which is FB-405: five gifts with no count and a bin as the only
 * control. A stepper written twice is a stepper that drifts, and this one already failed to reach the
 * second list once.
 *
 * A quantity of one shows the bin rather than a minus, because decrementing to nought and removing
 * are the same intention and two controls for it is one too many. **The plus is hidden for a gift** —
 * a gift is not something to ask for more of, and legacy hides it too.
 */
function LineStepper(props: {
  quantity: number
  isGift: boolean
  onRemove: () => void
  onChangeBy: (delta: number) => void
}) {
  return (
    <div className="flex items-center bg-secondary rounded-full p-1 border border-border/50">
      {props.quantity === 1 ? (
        <button
          type="button"
          data-line-remove
          aria-label={tt('Odobrať z košíka')}
          onClick={props.onRemove}
          className="w-11 h-11 rounded-full bg-white text-destructive flex items-center justify-center shadow-sm hover:bg-destructive hover:text-white active:scale-95 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          data-line-minus
          aria-label={tt('Menej')}
          onClick={() => props.onChangeBy(-1)}
          className="w-11 h-11 rounded-full bg-white text-foreground flex items-center justify-center shadow-sm hover:bg-primary hover:text-white active:scale-95 transition-colors"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
      )}
      <span className="w-8 text-center font-bold text-sm" data-line-quantity>
        {formatCount(props.quantity)}
      </span>
      {!props.isGift && (
        <button
          type="button"
          data-line-plus
          aria-label={tt('Viac')}
          onClick={() => props.onChangeBy(1)}
          className="w-11 h-11 rounded-full bg-white text-foreground flex items-center justify-center shadow-sm hover:bg-primary hover:text-white active:scale-95 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

function BasketLine(props: {
  entry: PricedLine
  onRemove: (key: string) => void
  onChangeBy: (key: string, delta: number) => void
}) {
  const entry = props.entry
  const removeLine = props.onRemove
  const changeBy = props.onChangeBy
  return (
            <div key={entry.key} className="flex gap-4 items-center" data-checkout-line={entry.line.productId}>
              <div className="flex-1 min-w-0">
                {/* §9.172 - the count in front of the name, and a gift said to be one. The stepper
                    beside the row carries the quantity, but a customer reading their order back
                    reads the *list*, and five pizzas looked like one. The daily-menu line has
                    printed its count this way since it was written; this one had not. */}
                <div className="font-bold">
                  {entry.line.quantity > 1 && `${formatCount(entry.line.quantity)}× `}
                  {entry.name}
                </div>
                {entry.line.giftRuleId !== undefined && (
                  <div
                    className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400"
                    data-line-gift={entry.line.giftRuleId}
                  >
                    🎁 <T>Darček</T>
                  </div>
                )}
                {entry.line.extras !== '' && (
                  <div className="text-xs text-amber-600 font-medium mt-0.5">+{entry.line.extras}</div>
                )}
                <div className="text-primary font-bold text-sm" data-line-total>
                  {formatCents(entry.totalCents)}
                </div>
              </div>
              <LineStepper
                quantity={entry.line.quantity}
                isGift={entry.line.giftRuleId !== undefined}
                onRemove={() => removeLine(entry.key)}
                onChangeBy={(delta) => changeBy(entry.key, delta)}
              />
            </div>
  )
}
